import i18next from 'i18next';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { updateSegment } from 'common/api/v1/segmentsLayout';
import {
    SEGMENT_COLOR,
    SEGMENT_STROKE_COLOR,
} from 'common/components/maps/constants';
import { setCeilZone } from 'common/components/segment/asceHelpers';
import {
    POLYGON_MAP,
    POLYGON_OBSTACLE,
    POLYGON_RECTANGLE,
    POLYGON_SEGMENT,
    POLYGON_CIRCLE,
} from 'common/components/segment/constants';
import {
    buildGroups,
    checkCallback,
    checkCallbackError,
    formatSegment,
    getDictionaryById,
    getModulesGroupV2,
    markGroupEdges,
    prepareModulesGroups,
} from 'common/components/segment/helpers';
import { showReponseErrorsAsAlert } from 'common/utils/helpers';
import {
    getMinMaxCols,
    getMinMaxRows,
} from 'common/utils/helpers/systemsValues';
import showToast from 'common/utils/showToast';

import {
    INITIALIZE_VALUES,
    EDIT_SEGMENT,
    EDIT_SEGMENT_FAILURE,
    EDIT_SEGMENT_SUCCESS,
    FETCH_OFFER_PANELS_SUCCESS,
} from '../actionTypes';
import {
    detectFieldChanges,
    getNewModules,
    getObstaceZIndex,
} from '../helpers';
import { panelLayoutV2Actions } from '../reducer';
import * as selectors from '../selectors';

import handleCollition from './handleCollition';
import updateSegments from './updateSegments';

const buildSegmentPayload = ({
    commercialOfferId,
    google,
    mapValue,
    presetConfiguration,
    segments,
    selectedSegment,
    values,
}) => {
    const {
        algorithm,
        azimuth,
        beam_spacing,
        building_heading,
        building_height,
        category,
        ceil_tilt,
        col_spacing,
        drawing_type,
        fill_algorithm,
        frame_col_spacing,
        frame_cols_number,
        frame_row_spacing,
        frame_rows_number,
        front_base_height,
        gclamp_type,
        height,
        is_custom_module,
        lfoot_supports_width,
        name,
        orientation,
        outer_rail_length,
        panel,
        parent_field_segment,
        plate_support_length,
        polygon,
        preset_configuration,
        rail_spacing,
        rail_support_length,
        row_spacing,
        safe_zone,
        seam_distance,
        selectedModule,
        sheet_gauge,
        sloof_type,
        solar_module,
        solar_modules_number,
        support_length,
        system,
        system_uid,
        tilt,
        type_application,
        zIndex,
    } = values;

    const quantity =
        fill_algorithm === 'fill' ? Infinity : solar_modules_number;

    let assigned = 0;
    let ceilZonedGroups = [];
    let defaultOfferPanelValues = {
        color: SEGMENT_COLOR,
        id: null,
        stroke_color: SEGMENT_STROKE_COLOR,
    };
    let groups = [];
    let module = null;
    let newPanel = null;
    let solar_modules = [];

    if (selectedModule) module = selectedModule;
    if (is_custom_module) {
        module = panel;
        newPanel = {
            ...module,
            brand: module.brandName,
            code: module.name,
            max_power: module.watts,
        };
    }

    if (module) {
        defaultOfferPanelValues = {
            ...defaultOfferPanelValues,
            ...module,
        };
    }

    const { color, stroke_color } = defaultOfferPanelValues;
    const { total_area } = selectedSegment;

    const solarCharacterization = {
        assigned,
        azimuth,
        ceil_tilt,
        col_spacing,
        frame_col_spacing,
        frame_cols_number,
        frame_row_spacing,
        frame_rows_number,
        height: height || 0,
        orientation,
        polygon: JSON.stringify(polygon),
        quantity,
        row_spacing,
        safe_zone: parseFloat(safe_zone) || 0,
        tilt,
        total_area,
        zIndex,
    };

    if (quantity > 0) {
        const obstacles = segments.filter(
            (segment) => segment.type === POLYGON_OBSTACLE,
        );

        const [minCols, maxCols] = getMinMaxCols(system_uid, panel.width);
        const [minRows, maxRows] = getMinMaxRows(system_uid);

        const { boxes, grouped } = getNewModules({
            algorithm,
            building_heading: building_heading,
            data: {
                ...solarCharacterization,
                minRows,
                minCols,
                maxRows,
                maxCols,
            },
            google,
            map: mapValue,
            module,
            obstacles,
            sloof_type,
        });

        let newGrouped = grouped;

        if (fill_algorithm === 'fill') {
            const newBoxes = boxes.map((box) => ({ ...box, enable: true }));
            newGrouped = buildGroups(newBoxes);
        }

        const modules_groups = getModulesGroupV2(newGrouped);
        groups = prepareModulesGroups(modules_groups);

        if (drawing_type === POLYGON_RECTANGLE) {
            const markedGroupEdges = markGroupEdges(modules_groups);

            ceilZonedGroups = setCeilZone({
                buildingHeight: building_height,
                ceilTilt: ceil_tilt,
                google,
                groups: markedGroupEdges,
                heading: building_heading,
                polygon,
                sloofType: sloof_type,
            });
        }

        solar_modules = boxes.map((module) => ({
            cell: module.cell,
            col: module.col,
            group: module.group,
            is_enable: fill_algorithm === 'fill' ? true : module.enable,
            row: module.row,
            solar_module_points: JSON.stringify(
                module.paths.map((point) => ({
                    x: point[1],
                    y: point[0],
                })),
            ),
        }));
    }

    const field_segments = [
        {
            name,
            polygon: polygon.map((point) => ({
                x_coordinate: point[1],
                y_coordinate: point[0],
            })),
            solar_modules,
        },
    ];

    console.log(presetConfiguration);

    const solution_fields = {
        beam_spacing,
        front_base_height,
        lfoot_supports_width,
        outer_rail_length,
        plate_support_length,
        rail_spacing,
        rail_support_length,
        seam_distance,
        sheet_gauge,
        support_length,
    };

    const { hasChanges = false } = detectFieldChanges(
        presetConfiguration,
        solution_fields,
    );

    const additionalCustomInputs = () => {
        if (preset_configuration && !hasChanges)
            return { preset_configuration };
        return { ...solution_fields };
    };

    const payload = {
        asce_metadata: ceilZonedGroups,
        commercial_offer: commercialOfferId,
        custom_inputs: {
            algorithm,
            building_heading,
            building_height,
            category,
            ceil_tilt,
            drawing_type,
            fill_algorithm,
            gclamp_type,
            panel_orientation: orientation,
            sloof_type,
            system_uid,
            tilt,
            type_application,
            ...additionalCustomInputs(),
        },
        field_segments,
        groups,
        height: building_height,
        is_custom_module,
        panel: newPanel,
        parent_field_segment,
        segment_color: color,
        segment_stroke_color: stroke_color,
        system,
        system_uid: system_uid || null,
        ...solarCharacterization,
        quantity: solar_modules.filter((item) => item.enabled).length,
    };

    if (!is_custom_module) {
        payload.solar_module = solar_module;
    }

    return payload;
};

const buildObstaclePayload = ({
    commercialOfferId,
    google,
    segments,
    selectedSegment,
    values,
}) => {
    const { height, name, parent_field_segment, polygon, safe_zone, type } =
        values;
    const { total_area, drawing_type } = selectedSegment;

    const zIndex = getObstaceZIndex({
        google,
        polygon,
        segments,
        selectedSegment,
    });

    const payload = {
        azimuth: 0,
        col_spacing: 0,
        commercial_offer: commercialOfferId,
        custom_inputs: { building_height: height || 0, drawing_type },
        field_segments: [
            {
                name,
                polygon: polygon.map((point) => ({
                    x_coordinate: point[1],
                    y_coordinate: point[0],
                })),
            },
        ],
        frame_col_spacing: 0,
        frame_cols_number: 0,
        frame_row_spacing: 0,
        frame_rows_number: 0,
        height: height || 0,
        name,
        orientation: 'P',
        parent_field_segment,
        polygon: JSON.stringify(polygon),
        row_spacing: 0,
        safe_zone: parseFloat(safe_zone) || 0,
        segment_color: '#8c8c8c',
        segment_stroke_color: '#11EFD9',
        tilt: 0,
        total_area,
        type,
        zindex: zIndex,
    };

    return payload;
};

const buildImagePayload = ({ commercialOfferId, selectedSegment, values }) => {
    const {
        height,
        image,
        name,
        opacity,
        parent_field_segment,
        polygon,
        rotate,
    } = values;
    const { total_area } = selectedSegment;

    const payload = {
        azimuth: 0,
        col_spacing: 0,
        commercial_offer: commercialOfferId,
        custom_inputs: {},
        field_segments: [
            {
                name,
                polygon: polygon.map((point) => ({
                    x_coordinate: point[1],
                    y_coordinate: point[0],
                })),
            },
        ],
        frame_col_spacing: 0,
        frame_cols_number: 0,
        frame_horizontal_number: 0,
        frame_row_spacing: 0,
        frame_rows_number: 0,
        frame_vertical_number: 0,
        height: height || 0,
        image,
        name,
        opacity,
        orientation: 'P',
        parent_field_segment,
        polygon: JSON.stringify(polygon),
        rotate: rotate ? parseFloat(rotate) : 0,
        row_spacing: 0,
        segment_color: '#8c8c8c',
        segment_stroke_color: '#11EFD9',
        solar_module: null,
        tilt: 0,
        total_area,
        type: POLYGON_MAP,
    };
    return payload;
};

export default (config) => (dispatch, getState) => {
    dispatch(panelLayoutV2Actions[EDIT_SEGMENT]());

    const { callback, commercialOfferId, google, mapRef, mapValue, values } =
        config;
    const { id: segmentId, parent_field_segment, type } = values;

    const state = getState();
    const segments = selectors.getSegmentsData(state);
    const presetConfiguration = selectors.getPresetConfiguration(state);
    const segmentsDictionary = getDictionaryById(segments);
    const selectedSegment = segmentsDictionary[segmentId];

    let payload = null;

    if (type === POLYGON_SEGMENT) {
        payload = buildSegmentPayload({
            commercialOfferId,
            google,
            mapValue,
            presetConfiguration,
            segments,
            selectedSegment,
            values,
        });
    }

    if ([POLYGON_OBSTACLE, POLYGON_CIRCLE].includes(type)) {
        payload = buildObstaclePayload({
            commercialOfferId,
            google,
            segments,
            selectedSegment,
            values,
        });
    }

    if (type === POLYGON_MAP) {
        payload = buildImagePayload({
            commercialOfferId,
            selectedSegment,
            values,
        });
    }

    updateSegment(segmentId, payload)
        .then((response) => {
            const segmentData = get(response, 'data', {});
            const formatedSegment = formatSegment(
                segmentData,
                parent_field_segment,
            );
            const updatedSegment = formatedSegment;
            dispatch(
                panelLayoutV2Actions[EDIT_SEGMENT_SUCCESS](updatedSegment),
            );

            const updatedSegments = segments.map((segment) =>
                segment.id === updatedSegment.id
                    ? { ...segment, ...updatedSegment, open: true }
                    : { ...segment, open: false },
            );
            dispatch(updateSegments(updatedSegments));

            if (segmentData.type === POLYGON_SEGMENT) {
                const initialValues = selectors.getInitialValues(state);

                if (!isEmpty(initialValues)) {
                    dispatch(
                        panelLayoutV2Actions[INITIALIZE_VALUES](updatedSegment),
                    );
                }
            }

            if (segmentData.type === POLYGON_SEGMENT) {
                const panels = updatedSegments
                    .filter((segment) => segment.type === POLYGON_SEGMENT)
                    .map((segment) => segment.panel);
                dispatch(
                    panelLayoutV2Actions[FETCH_OFFER_PANELS_SUCCESS]({
                        panels,
                    }),
                );
            }

            if ([POLYGON_OBSTACLE, POLYGON_CIRCLE].includes(segmentData.type)) {
                dispatch(
                    handleCollition({
                        callback: (id, modules) =>
                            checkCallback(id, modules, mapRef),
                        callbackError: (id, values) =>
                            checkCallbackError(id, values, mapRef),
                        commercialOfferId,
                        google,
                        mapValue,
                        obstacle: updatedSegment,
                    }),
                );
            }

            if (callback) callback();

            showToast({ body: i18next.t('Segment changes have been saved') });
        })
        .catch((error) => {
            dispatch(
                panelLayoutV2Actions[EDIT_SEGMENT_FAILURE](
                    error?.response?.data?.errors,
                ),
            );
            showReponseErrorsAsAlert(dispatch, error.response);
        });
};
